import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import { flatten, deepen } from '../../versa/record.js';
import { notify } from '../../versa/msgbox.js';
import view from '../views/companies.js';
import form from '../forms/companies.js';
import * as types from '../types.js';

const companies = function()
{

	let companies_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_cities, this.queryAll()).then((data) => db.set('cities', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			// /companies filtra por grupos do usuário; empresa nova não tem binding e sumiria da lista.
			// Tela de cadastro de empresas lista todas as ativas (igual GET /companies/all).
			return await api.call(api.list_all_companies, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_company, ...JSON.parse(id));
			_record = flatten(_record, 'address');
			if (_record.address_city_id) {
				const c = await db.get('cities', _record.address_city_id);
				if (c) {
					_record['address_state'] = c.state;
					_record['address_city_id'] = c.city;
				}
			}
			_record = flatten(_record, 'custody_emit_address');
			if (_record.custody_emit_address_city_id) {
				const c = await db.get('cities', _record.custody_emit_address_city_id);
				if (c) {
					_record['custody_emit_address_state'] = c.state;
					_record['custody_emit_address_city_id'] = c.city;
				}
			}
			_record.output_orders_require_full_fulfillment =
				_record.output_orders_require_full_fulfillment === true || _record.output_orders_require_full_fulfillment === '1'
					? '1'
					: '0';
			_record.input_collector_strict =
				_record.input_collector_strict === true || _record.input_collector_strict === '1' ? '1' : '0';
			return _record;
		},
		async saveRecord(data, old_data)
		{
			const sel = '#company';
			const normalize = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
			/**
			 * Heurística: quando a UF do formulário está vazia, infere a partir dos 5 primeiros dígitos do CEP
			 * (evita enviar o nome do município em city_id; complementar conforme necessidade operacional).
			 */
			const inferUfFromCep = (cep) => {
				const d = String(cep || '').replace(/\D/g, '');
				if (d.length < 5) return '';
				const n5 = parseInt(d.slice(0, 5), 10);
				if (n5 >= 77000 && n5 <= 77999) return 'TO';
				return '';
			};
			/**
			 * Converte nome de cidade + UF no código IBGE (7 dígitos). Nunca retorna o nome; em falta, notifica e devolve null.
			 * Se a API não tiver municípios da UF (lista vazia), usa ViaCEP pelo CEP (campo ibge), sem depender da tabela cities.
			 */
			const VIACEP_BASE = 'https://viacep.com.br/ws';
			const ibgeFromViaCep = async (zipDigits, cityNorm, ufExpected) => {
				const cep = String(zipDigits || '').replace(/\D/g, '');
				if (cep.length !== 8) return null;
				try {
					const r = await fetch(`${VIACEP_BASE}/${cep}/json/`);
					const j = await r.json();
					if (j?.erro || !j?.ibge) return null;
					const ibge = String(j.ibge).replace(/\D/g, '');
					if (!/^\d{7}$/.test(ibge)) return null;
					const ufv = String(ufExpected || '').toUpperCase().trim().slice(0, 2);
					if (ufv && j.uf && String(j.uf).toUpperCase().trim().slice(0, 2) !== ufv) return null;
					const locN = normalize(j.localidade || '');
					const exp = (cityNorm || '').trim();
					if (!exp || !locN) return ibge;
					if (
						locN === exp
						|| locN.startsWith(exp)
						|| exp.startsWith(locN)
						|| (exp.length >= 3 && locN.includes(exp))
						|| (locN.length >= 3 && exp.includes(locN))
					)
						return ibge;
					return null;
				} catch (_) {
					return null;
				}
			};
			const resolveCityId = async (state, cityVal, zipHint) => {
				if (cityVal == null || String(cityVal).trim() === '') return cityVal;
				const v = String(cityVal).trim();
				if (/^\d{7}$/.test(v)) return v;
				let u = String(state || '').toUpperCase().trim().slice(0, 2);
				if (!u || u.length < 2) u = inferUfFromCep(zipHint) || '';
				if (!u || u.length < 2) {
					notify('Preencha a UF (estado) do endereço — necessário para localizar o código IBGE do município.');
					return null;
				}
				const nv = normalize(v);
				let cities = [];
				try {
					const res = await api.call(api.list_cities, `?filter=state*eq*${encodeURIComponent(u)}&limit=500`);
					cities = Array.isArray(res) ? res : [];
				} catch (e) {
					notify(e?.message || String(e));
					return null;
				}
				let c = cities.length
					? cities.find(x => normalize(x?.city || '') === nv)
					: null;
				if (!c && cities.length)
					c = cities.find(x => {
						const nc = normalize(x?.city || '');
						return nc.startsWith(nv) || (nv.length >= 3 && nc.includes(nv));
					});
				if (c) return String(c.id);
				const via = await ibgeFromViaCep(zipHint, nv, u);
				if (via) return via;
				if (!cities.length) {
					notify(
						`Não há municípios da UF ${u} disponíveis na base do sistema e o CEP não retornou um código IBGE compatível com “${v}”. Verifique o CEP ou peça a carga da tabela de cidades (IBGE).`,
						'Atenção'
					);
				} else {
					notify(`Município "${v}" não encontrado na UF ${u}. Ajuste o nome ou a UF.`);
				}
				return null;
			};
			const getAddr = (suffix) => {
				const el = document.querySelector(`${sel} #address_${suffix}`);
				return el?.value != null ? String(el.value).trim() : '';
			};
			const getEmitAddr = (suffix) => {
				const el = document.querySelector(`${sel} #custody_emit_address_${suffix}`);
				return el?.value != null ? String(el.value).trim() : '';
			};
			const getZipFromDom = () => (getAddr('zipcode').replace(/\D/g, ''));
			const street = (data.address_street || getAddr('street')).trim();
			const number = (data.address_number || getAddr('number')).trim();
			const complement = (data.address_complement || getAddr('complement')).trim() || null;
			const district = (data.address_district || getAddr('district')).trim();
			const zipcode = (data.address_zipcode && String(data.address_zipcode).replace(/\D/g, '')) || getZipFromDom();
			const stRaw = (getAddr('state') || data.address_state || '');
			const state = stRaw ? stRaw.toUpperCase().trim().slice(0, 2) : '';
			let cityId = data.address_city_id != null && data.address_city_id !== '' ? data.address_city_id : getAddr('city_id');
			if (cityId != null && String(cityId).trim() !== '') {
				if (!/^\d{7}$/.test(String(cityId).trim())) {
					const resolved = await resolveCityId(state, cityId, zipcode);
					if (resolved == null) return false;
					cityId = resolved;
				} else {
					cityId = String(cityId).trim();
				}
			}
			const rawAddrId = data.address_id;
			const addressIdNum = (rawAddrId != null && rawAddrId !== '' && !Number.isNaN(Number(rawAddrId))) ? Number(rawAddrId) : null;

			const emitStreet = (data.custody_emit_address_street || getEmitAddr('street')).trim();
			const emitNumber = (data.custody_emit_address_number || getEmitAddr('number')).trim();
			const emitComplement = (data.custody_emit_address_complement || getEmitAddr('complement')).trim() || null;
			const emitDistrict = (data.custody_emit_address_district || getEmitAddr('district')).trim();
			const emitZip = (data.custody_emit_address_zipcode && String(data.custody_emit_address_zipcode).replace(/\D/g, '')) || getEmitAddr('zipcode').replace(/\D/g, '');
			const emitStRaw = (getEmitAddr('state') || data.custody_emit_address_state || '');
			const emitState = emitStRaw ? emitStRaw.toUpperCase().trim().slice(0, 2) : '';
			let emitCityId = data.custody_emit_address_city_id != null && data.custody_emit_address_city_id !== '' ? data.custody_emit_address_city_id : getEmitAddr('city_id');
			if (emitCityId != null && String(emitCityId).trim() !== '') {
				if (!/^\d{7}$/.test(String(emitCityId).trim())) {
					const resolvedEm = await resolveCityId(emitState, emitCityId, emitZip);
					if (resolvedEm == null) return false;
					emitCityId = resolvedEm;
				} else {
					emitCityId = String(emitCityId).trim();
				}
			}
			const rawEmitAddrId = data.custody_emit_address_id;
			const emitAddrIdNum = (rawEmitAddrId != null && rawEmitAddrId !== '' && !Number.isNaN(Number(rawEmitAddrId))) ? Number(rawEmitAddrId) : null;

			let _record = {
				nick: data.nick,
				name: data.name,
				cnpj: data.cnpj,
				ie: data.ie,
				ie_type: data.ie_type,
				phone: data.phone,
				inventory_strategy: data.inventory_strategy,
				crt: data.crt,
				pis: Number(data.pis),
				cofins: Number(data.cofins),
				estimated_taxes: Number(data.estimated_taxes),
				invoice_series: Number(data.invoice_series),
				authorized_parties: data.authorized_parties,
				logo: data.logo,
				fiscal_modality: data.fiscal_modality || '0',
				custody_warehouse_cnpj: data.fiscal_modality === '1' ? (data.custody_warehouse_cnpj || null) : null,
				custody_emit_name: data.fiscal_modality === '1' ? ((data.custody_emit_name || '').trim() || null) : null,
				custody_emit_ie: data.fiscal_modality === '1' ? ((data.custody_emit_ie || '').trim() || null) : null,
				custody_emit_phone: data.fiscal_modality === '1' ? ((data.custody_emit_phone || '').trim() || null) : null,
				custody_emit_crt: data.fiscal_modality === '1' ? ((data.custody_emit_crt || '').trim() || null) : null,
				inv_allow_expired: data.inv_allow_expired == '1',
				inv_require_lot: data.inv_require_lot == '1',
				inv_by_program: data.inv_by_program == '1',
				inv_class_blocking: data.inv_class_blocking == '1',
				inv_conference_tolerance: Number(data.inv_conference_tolerance || 0),
				pkg_use_hierarchy: data.pkg_use_hierarchy == '1',
				pkg_unitarization_enabled: data.pkg_unitarization_enabled == '1',
				pkg_optimize_picking: data.pkg_optimize_picking == '1',
				pkg_allow_tertiary_break: data.pkg_allow_tertiary_break == '1',
				pkg_allow_secondary_break: data.pkg_allow_secondary_break == '1',
				pkg_validate_gtin: data.pkg_validate_gtin == '1',
			pkg_rollback_legacy: data.pkg_rollback_legacy == '1',
			out_enable_box_control: data.out_enable_box_control == '1',
			out_allow_partial_conference: data.out_allow_partial_conference == '1',
			out_require_divergence_auth: data.out_require_divergence_auth == '1',
			out_conference_collector_validation: data.out_conference_collector_validation == '1',
			out_conference_blind_by_packaging: data.out_conference_blind_by_packaging == '1',
			out_dock_expedition_enabled: data.out_dock_expedition_enabled == '1',
			input_orders_require_payment_condition: data.input_orders_require_payment_condition == '1',
			input_orders_require_approval: data.input_orders_require_approval == '1',
			output_orders_require_full_fulfillment:
					data.output_orders_require_full_fulfillment === true
					|| data.output_orders_require_full_fulfillment === '1'
					|| data.output_orders_require_full_fulfillment === 1,
				input_collector_strict: data.input_collector_strict == '1',
				nfe_inf_cpl_entrada: (data.nfe_inf_cpl_entrada && String(data.nfe_inf_cpl_entrada).trim()) ? String(data.nfe_inf_cpl_entrada).trim() : null,
				nfe_inf_cpl_saida: (data.nfe_inf_cpl_saida && String(data.nfe_inf_cpl_saida).trim()) ? String(data.nfe_inf_cpl_saida).trim() : null,
				nfe_inf_resp_tec_cnpj: (() => {
					const d = String(data.nfe_inf_resp_tec_cnpj || '').replace(/\D/g, '');
					return d.length === 14 ? d : null;
				})(),
				nfe_inf_resp_tec_contato: (data.nfe_inf_resp_tec_contato && String(data.nfe_inf_resp_tec_contato).trim()) ? String(data.nfe_inf_resp_tec_contato).trim() : null,
				nfe_inf_resp_tec_email: (data.nfe_inf_resp_tec_email && String(data.nfe_inf_resp_tec_email).trim()) ? String(data.nfe_inf_resp_tec_email).trim() : null,
				nfe_inf_resp_tec_fone: (data.nfe_inf_resp_tec_fone && String(data.nfe_inf_resp_tec_fone).trim()) ? String(data.nfe_inf_resp_tec_fone).trim() : null,
			};
			if (addressIdNum || street || number || district || zipcode || cityId) {
				_record['address'] = {
					id: addressIdNum,
					street: street || null,
					number: number || null,
					complement: complement,
					district: district || null,
					zipcode: zipcode || null,
					city_id: cityId || null
				};
			}
			if (data.fiscal_modality === '1') {
				_record['custody_emit_address'] = (emitAddrIdNum || emitStreet || emitNumber || emitDistrict || emitZip || emitCityId)
					? {
						id: emitAddrIdNum,
						street: emitStreet || null,
						number: emitNumber || null,
						complement: emitComplement,
						district: emitDistrict || null,
						zipcode: emitZip || null,
						city_id: emitCityId || null
					}
					: null;
			} else {
				_record['custody_emit_address'] = null;
			}
			if (old_data)
				return await api.call(api.update_company, data.id, _record);
			else
				return await api.call(api.insert_company, _record);
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Identificador da empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'nick', label: 'Apelido', types: [], placeholder: '', title: 'Nome curto da empresa' },
		{ index: 3, field: 'name', label: 'Nome', types: [], placeholder: '', title: 'Nome ou razão social' },
		{ index: 4, field: 'cnpj', label: 'CNPJ', types: [types.cnpj()], placeholder: '00.000.000/0000-00', title: 'Número do CNPJ' },
		{ index: 5, field: 'ie', label: 'IE', types: [types.ie()], placeholder: '000.000.000', title: 'Inscrição Estadual' },
		{ index: 6, field: 'ie_type', label: 'Tipo IE', types: [types.opt_ie_type(), types.datalist()], placeholder: '', title: 'Tipo de Inscrição Estadual' },
		//{ index: 7, field: 'iest', label: 'IEST', types: [], placeholder: '', title: 'Inscrição Estadual do Substituto Tributário' },
		{ index: 8, field: 'phone', label: 'Fone', types: [types.phone()], placeholder: '+00 (00) 0000-0000', title: 'Telefone' },
		{ index: 10, field: 'inventory_strategy', label: 'Inventário', types: [types.opt_inventory_strategy(), types.datalist()], placeholder: '', title: 'Estratégia de inventário' },
		{ index: 11, field: 'crt', label: 'CRT', types: [types.opt_crt(), types.datalist()], placeholder: '', title: 'Código de Regime Tributário' },
		{ index: 12, field: 'pis', label: 'PIS', types: [types.number(2)], placeholder: '0,00', title: 'Alíquota de PIS' },
		{ index: 13, field: 'cofins', label: 'COFINS', types: [types.number(2)], placeholder: '0,00', title: 'Alíquota de COFINS' },
		{ index: 14, field: 'estimated_taxes', label: 'Tributos', types: [types.number(2)], placeholder: '0,00', title: 'Percentual estimado de tributos federais, estaduais e municipais' },
		{ index: 15, field: 'invoice_series', label: 'Série', types: [types.number(0, 2)], placeholder: '0', title: 'Série da NFe' },
		{ index: 16, field: 'authorized_parties', label: 'Autorizados', types: [types.array()], placeholder: '', title: 'CPF ou CNPJ das pessoas autorizadas a acessar o XML da NFe' },
		{ index: 17, field: 'fiscal_modality', label: 'Modalidade', types: [], placeholder: '', title: 'Modalidade fiscal: Normal ou Fiel depositário' },
		{ index: 18, field: 'custody_warehouse_cnpj', label: 'CNPJ armazenador', types: [types.cnpj()], placeholder: '00.000.000/0000-00', title: 'CNPJ que armazena (quando fiel depositário)' },
	];

	companies_crud.run(
		'companies',
		'Empresas',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs
	);

};

export default companies;

